import React from 'react';
import { createSelector } from 'reselect';
import get from 'lodash/get';
import { DeletedMetricWithRemove } from '@components/common/DeletedMetric';
import { DimensionDropdown } from '@components/noc-metric-panel';
import { getMetricMetaByKey, getPercentileMetrics } from '@src/utils/metrics/metricHelpers';
import { isAppModule } from '@utils/constants';
import {
  trendsTableDataSortSelector,
  dataSelector,
  dimensionSelector,
  metricSelector,
} from '@store/noc/trendsData/selectors';
import { initThresholdSelector, isThresholdDisabledSelector } from '@store/noc/threshold/selectors';
import { autoSortEnabledSelector } from '@store/noc/base/selectors';
import { Header, Cell, Scope } from '../common';
import { thresholdBaselineSelector, calcRowThreshold, autoSort } from '../common/utils';
import { ScopeWrapper, RightDiv } from '../styled';
import { currentMetricsMetaSelector } from '@store/common/selectors';

import type { ColumnShape } from 'react-base-table';

// main column (left, frozen)
const mainColSelector = createSelector(dimensionSelector, (dimension) => {
  const columns = [
    {
      title: dimension,
      dataKey: 'name',
      key: dimension,
      width: 400,
      frozen: true,
      resizable: true,
      headerRenderer: () => <DimensionDropdown />,
      cellRenderer: ({ cellData, rowData }) => (
        <ScopeWrapper>
          <Scope text={cellData} rowData={rowData} />
        </ScopeWrapper>
      ),
      align: 'left',
    },
  ];
  return columns;
});

// remaining metric based columns
const metricColumnSelector = createSelector(
  metricSelector,
  trendsTableDataSortSelector,
  currentMetricsMetaSelector,
  (_, errorData) => errorData,
  (_, __, deleteMetric) => deleteMetric,
  (metrics, res, allMetrics, errorData, deleteMetric) => {
    const { maxData }: any = res;
    const columns: ColumnShape[] = [];
    metrics.forEach((metric) => {
      const isExist = !!allMetrics[metric] && !errorData[metric];
      const metricInfo = !isExist ? {} : getMetricMetaByKey({ metricKey: metric });
      const max = maxData[metric];
      let text = metricInfo.displayName;
      if (getPercentileMetrics().includes(metric) && isAppModule) {
        text += ` (Avg)`;
      }
      const column = {
        title: text,
        dataKey: metric,
        key: metric,
        width: 180,
        maxData: max,
        headerRenderer: () =>
          !isExist ? (
            <DeletedMetricWithRemove
              onRemove={() => {
                deleteMetric(metric);
              }}
            />
          ) : (
            <Header metric={metricInfo} />
          ),
        cellRenderer: ({ cellData, column, rowData }) => {
          return !isExist ? (
            <RightDiv> - </RightDiv>
          ) : (
            <Cell
              cellData={cellData}
              column={column}
              rowData={rowData}
              unit={metricInfo.unit}
              max={max}
            />
          );
        },
        align: 'left',
        sortable: !isExist ? false : true,
      } as const;
      columns.push(column);
    });
    return columns;
  },
);

// combined columns
export const columnsSelector = (state, errorData, deleteMetric) =>
  createSelector(
    (state) => mainColSelector(state),
    (state) => metricColumnSelector(state, errorData, deleteMetric),
    (mainColumns, metricColumns) => [...mainColumns, ...metricColumns],
  )(state);

// map threshold data for faster lookup
export const thresholdSelector = createSelector(initThresholdSelector, (res) => {
  const thresholdList = res.data.thresholds || [];
  const values = {};

  thresholdList.forEach((row) => {
    values[row.dimension_attribute] = row;
  });
  return values;
});

// build data from response
export const tableDataSelector = createSelector(
  trendsTableDataSortSelector,
  thresholdSelector,
  thresholdBaselineSelector,
  metricSelector,
  isThresholdDisabledSelector,
  autoSortEnabledSelector,
  (res, thresholds, thresholdBaseline, metrics, isThresholdDisabled, isAutoSortEnabled) => {
    const { data, sort, total } = res;
    let records: any[] = [];

    records =
      data.map((rowData) => ({
        ...rowData,
        threshold: isThresholdDisabled
          ? {}
          : calcRowThreshold({
              rowData,
              metrics,
              thresholds,
              thresholdBaseline,
            }),
      })) || [];

    autoSort(isAutoSortEnabled, records);
    return { data: records, sort, total };
  },
);

export const warmingUpFiltersSelector = createSelector(dataSelector, (res) =>
  get(res, 'warmup', []).map((row) => ({
    id: row.fid || row.key,
    filter: { name: row.name },
    warmup: true,
  })),
);

export const trendsTotalSelector = createSelector(dataSelector, (res) => get(res, 'totals', []));
